import * as Popover from '@radix-ui/react-popover';
import { format } from 'date-fns';
import { useCallback, useId, useMemo, useState } from 'react';
import type { ClassNames } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { Button } from '~/chords/Button';
import { Icon } from '~/chords/Icon';
import { cn } from '~/chords/lib/cn';
import { type VariantProps } from '~/chords/lib/variants';

import { ChevronIcon } from '../ChevronIcon/ChevronIcon';
import { inputVariants } from '../Input/variants';
import { dayPickerClassNames } from './DatePicker.styles';
import type { DatePickerAppearance, DatePickerVariants } from './utils';
import {
  datePickerVariants,
  formatDate,
  formatTime,
  FUTURE_LIMIT,
  getDisabledDates,
  TODAY,
} from './utils';

export interface DatePickerProps
  extends Pick<
    VariantProps<typeof datePickerVariants>,
    'variant' | 'appearance'
  > {
  value?: Date;
  onApply: (date: Date) => void;
  /**
   * When provided, a clear button is shown while a date is selected. Clicking
   * it closes the calendar and calls `onClear`; the parent must reset `value`.
   */
  onClear?: () => void;
  minDate?: Date;
  allowFutureDates?: boolean;
  placeholder?: string;
  highlightToday?: boolean;
  variant?: DatePickerVariants;
  appearance?: DatePickerAppearance;
  timeOptions?: {
    isEnabled: boolean;
    label: string;
  };
}

export function DatePicker({
  onApply,
  onClear,
  value,
  minDate,
  allowFutureDates = true,
  placeholder = 'Select Date',
  highlightToday = false,
  variant = 'default',
  appearance = 'dropdown',
  timeOptions,
}: DatePickerProps) {
  const timeInputId = useId();

  const { t } = useTranslation('chords');
  const [open, setOpen] = useState(false);
  const [datetime, setDatetime] = useState<Date | undefined>(value);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      setDatetime(value);
    },
    [value],
  );

  const handleSelectDateAndTime = useCallback(
    (date: Date) => {
      const nextDatetime = new Date(date);

      if (datetime) {
        nextDatetime.setHours(
          datetime.getHours(),
          datetime.getMinutes(),
          datetime.getSeconds(),
          datetime.getMilliseconds(),
        );
      }

      setDatetime(nextDatetime);
    },
    [datetime],
  );

  const handleSelectDate = useCallback(
    (date: Date) => {
      if (timeOptions?.isEnabled) {
        handleSelectDateAndTime(date);

        return;
      }

      onApply(date);
      handleOpenChange(false);
    },
    [
      onApply,
      timeOptions?.isEnabled,
      handleSelectDateAndTime,
      handleOpenChange,
    ],
  );

  const handleSelectTime = useCallback(
    (time: string) => {
      if (!datetime || !time) {
        return;
      }

      const [hours, minutes] = time.split(':').map(Number);

      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return;
      }

      const nextDatetime = new Date(datetime);

      nextDatetime.setHours(
        hours,
        minutes,
        datetime.getSeconds(),
        datetime.getMilliseconds(),
      );

      setDatetime(nextDatetime);
    },
    [datetime],
  );

  const isPlaceholder = !value;

  const handleCancel = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const handleConfirm = useCallback(() => {
    if (!datetime) {
      return;
    }

    handleOpenChange(false);
    onApply(datetime);
  }, [datetime, handleOpenChange, onApply]);

  const disabledDates = useMemo(
    () => getDisabledDates(minDate, allowFutureDates),
    [minDate, allowFutureDates],
  );

  const classNames: Partial<ClassNames> = { ...dayPickerClassNames };

  if (highlightToday) {
    classNames.today = '[&>button]:underline [&>button]:underline-offset-4';
  }

  const chevronDirection = open ? 'up' : 'down';
  const { field, trigger, text, clearButton, trailingIcon } =
    datePickerVariants({
      variant,
      appearance,
      isPlaceholder,
    });

  const displayValue = useMemo(() => {
    if (timeOptions?.isEnabled) {
      return value ? (
        <span className="flex items-center gap-sm">
          {formatDate(value)}
          <span className="text-subtle">•</span>
          <span>{formatTime(value)}</span>
        </span>
      ) : (
        placeholder
      );
    }

    return formatDate(value) ?? placeholder;
  }, [value, placeholder, timeOptions?.isEnabled]);

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Anchor asChild>
        <div data-testid="date-picker-field" className={field()}>
          <Popover.Trigger asChild>
            <button
              type="button"
              data-testid="date-picker-trigger"
              data-date-picker-trigger=""
              aria-invalid={variant === 'error'}
              className={trigger()}
            >
              <span className={text()}>{displayValue}</span>
            </button>
          </Popover.Trigger>

          {onClear && value ? (
            <button
              type="button"
              data-testid="date-picker-clear"
              aria-label={t('datePicker.clearDate')}
              onClick={() => {
                onClear();
                setOpen(false);
              }}
              className={clearButton()}
            >
              <Icon type="xmark" size="sm" aria-hidden />
            </button>
          ) : null}

          <span aria-hidden className={trailingIcon()}>
            {appearance === 'dropdown' ? (
              <ChevronIcon direction={chevronDirection} />
            ) : null}

            {appearance === 'input' ? (
              <Icon type="calendar" variant="light" size="sm" />
            ) : null}
          </span>
        </div>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          className="z-10 mt-xs w-[18rem] overflow-hidden rounded-sm border bg-level-03 border-emphasis-subtle"
        >
          <div className="flex flex-col">
            <DayPicker
              showOutsideDays
              required={true}
              mode="single"
              captionLayout="dropdown"
              disabled={disabledDates}
              startMonth={minDate || TODAY}
              endMonth={allowFutureDates ? FUTURE_LIMIT : TODAY}
              selected={timeOptions?.isEnabled ? datetime : value}
              onSelect={handleSelectDate}
              numberOfMonths={1}
              classNames={classNames}
              formatters={{
                formatWeekdayName: (weekday) => format(weekday, 'ccccc'),
                formatMonthDropdown: (month) => format(month, 'MMMM'),
              }}
              data-testid="date-picker-calendar"
            />
            {timeOptions?.isEnabled ? (
              <div className="border-t-px p-lg bg-level-02 border-emphasis-subtle">
                <label
                  htmlFor={timeInputId}
                  className="mb-md block text-default"
                >
                  {timeOptions?.label}
                </label>
                <div className="relative w-full">
                  <input
                    id={timeInputId}
                    data-testid="date-time-picker-time-input"
                    aria-invalid={variant === 'error'}
                    disabled={!datetime}
                    type="time"
                    value={datetime ? format(datetime, 'HH:mm') : ''}
                    onChange={(e) => handleSelectTime(e.target.value)}
                    className={cn(
                      inputVariants({
                        state: variant,
                        rounded: 'xsmall',
                        size: 'large',
                      }),
                      'pr-xl',
                      '[&::-webkit-calendar-picker-indicator]:absolute',
                      '[&::-webkit-calendar-picker-indicator]:inset-y-0',
                      '[&::-webkit-calendar-picker-indicator]:right-0',
                      '[&::-webkit-calendar-picker-indicator]:h-full',
                      '[&::-webkit-calendar-picker-indicator]:w-xl',
                      '[&::-webkit-calendar-picker-indicator]:cursor-pointer',
                      '[&::-webkit-calendar-picker-indicator]:[opacity:0]',
                    )}
                    aria-describedby={
                      !datetime ? `${timeInputId}-description` : undefined
                    }
                  />
                  {!datetime ? (
                    <span id={`${timeInputId}-description`} className="sr-only">
                      {t('datePicker.disabledDescription')}
                    </span>
                  ) : null}
                  <span
                    className={cn(
                      'pointer-events-none absolute right-md top-1/2 -translate-y-1/2 text-default',
                      { 'text-disabled': !datetime },
                    )}
                  >
                    <Icon
                      type="clock"
                      variant="light"
                      size="sm"
                      aria-hidden="true"
                    />
                  </span>
                </div>
                <div className="mt-lg flex justify-end gap-md">
                  <Button
                    type="button"
                    onClick={handleCancel}
                    variant="tertiary"
                  >
                    {t('datePicker.cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!datetime}
                  >
                    {t('datePicker.confirm')}
                  </Button>
                </div>
              </div>
            ) : undefined}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

DatePicker.displayName = 'DatePicker';
